import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type HeroCategory = 'homes' | 'plots' | 'commercial';

export const HERO_SUBTYPES: Record<HeroCategory, { label: string; value: string }[]> = {
  homes: [
    { label: 'House', value: 'villa' },
    { label: 'Flat', value: 'flat' },
    { label: 'Upper Portion', value: 'upper_portion' },
    { label: 'Lower Portion', value: 'lower_portion' },
    { label: 'Farm House', value: 'farm_house' },
    { label: 'Room', value: 'room' },
    { label: 'Penthouse', value: 'penthouse' },
  ],
  plots: [
    { label: 'Residential Plot', value: 'residential' },
    { label: 'Commercial Plot', value: 'commercial' },
    { label: 'Agricultural Land', value: 'agricultural' },
    { label: 'Industrial Land', value: 'industrial' },
    { label: 'Plot File', value: 'plot_file' },
    { label: 'Plot Form', value: 'plot_form' },
  ],
  commercial: [
    { label: 'Office', value: 'office' },
    { label: 'Shop', value: 'retail' },
    { label: 'Warehouse', value: 'warehouse' },
    { label: 'Factory', value: 'factory' },
    { label: 'Building', value: 'building' },
    { label: 'Other', value: 'other' },
  ],
};

const CAT_LABEL: Record<HeroCategory, string> = {
  homes: 'Homes',
  plots: 'Plots',
  commercial: 'Commercial',
};

interface Props {
  category: HeroCategory;
  setCategory: (c: HeroCategory) => void;
  subtype: string;
  setSubtype: (v: string) => void;
}

export function HeroPropertyTypePopover({ category, setCategory, subtype, setSubtype }: Props) {
  const [open, setOpen] = useState(false);
  const current = HERO_SUBTYPES[category].find(s => s.value === subtype);
  const triggerLabel = current ? current.label : CAT_LABEL[category];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 px-3 rounded-md border border-border/60 text-sm bg-background flex items-center justify-between hover:border-primary/40 transition-colors"
        >
          <span className="font-medium truncate">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-4">
        <div className="flex gap-5 border-b mb-3">
          {(Object.keys(HERO_SUBTYPES) as HeroCategory[]).map(c => {
            const active = c === category;
            return (
              <button
                key={c}
                type="button"
                onClick={() => { setCategory(c); setSubtype(''); }}
                className={cn(
                  'pb-2 -mb-px text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors',
                  active ? 'text-primary border-primary' : 'text-muted-foreground border-transparent hover:text-foreground'
                )}
              >
                {CAT_LABEL[c]}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {HERO_SUBTYPES[category].map(s => {
            const active = s.value === subtype;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => { setSubtype(s.value); setOpen(false); }}
                className={cn(
                  'h-9 rounded-md border text-xs font-medium transition-colors',
                  active
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:border-primary/40'
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t">
          <button
            type="button"
            onClick={() => setSubtype('')}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Reset
          </button>
          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => setOpen(false)}>Close</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
